import { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { appContext, serverContext } from '../App';
import MaterialTable, { MTableToolbar } from 'material-table'
import PatchedPagination from './PatchedPagination';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import AisTableToolbar from './AisTableToolbar';
import { useAisData } from '../customEffects';
import { convertLatitudeToDMS, convertLongitudeToDMS } from '../utils';
import ReactCountryFlag from "react-country-flag";
import { tableIcons } from '../commonTypes';
import { useTableTheming } from './useTableTheming';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';

const aisTimestampFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatAisTimestamp = value => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid Date');
  }

  const parts = aisTimestampFormatter.formatToParts(date);
  const getPart = type => parts.find(part => part.type === type)?.value;

  return `${getPart('month')} ${getPart('day')} ${getPart('year')}, ${getPart('hour')}:${getPart('minute')}`;
};

// --- Define the FixTypeDisplay component ---
const FixTypeDisplay = ({ fixType }) => {
  const getFixTypeString = (type) => {
    switch (type) {
      case 0: return 'Undefined';
      case 1: return 'GPS';
      case 2: return 'GLONASS';
      case 3: return 'GPS + GLONASS';
      case 4: return 'Loran-C';
      case 5: return 'Chayka';
      case 6: return 'Integrated';
      case 7: return 'Surveyed';
      case 8: return 'Galileo';
      case 15: return 'N/A';
      default: return 'Unknown';
    }
  };

  const fixTypeString = getFixTypeString(fixType);
  const originalValue = fixType !== undefined && fixType !== null ? fixType : 'N/A';

  return (
    <Tooltip title={`Fix Type: ${originalValue}`}>
      <span>{fixTypeString}</span>
    </Tooltip>
  );
};
// --- End of FixTypeDisplay component ---


const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiPaper-root': {
      paddingLeft: '0.5em',
      paddingRight: '0.5em',
    }
  },
  denseTable: {
    '& .MuiTableCell-root': {
      padding: '0.2em', // Reduced cell padding
    },
    '& .MuiTableRow-root': {
      height: '32px', // Reduced row height
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      padding: '0.2em', // Reduced header padding
    },
    '& .MuiToolbar-root': {
      padding: '0.2em', // Reduced toolbar padding
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold', // Ensures header text is bold
    },
  },
  mtToolbarNoWrap: {
    '@global': {
      /* Keep the whole toolbar on one line */
      '[class*="MTableToolbar-toolbar"]': {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: 8,
      },
      /* Let the LEFT side (title/search) shrink, so it doesn't push actions down */
      '[class*="MTableToolbar-root"]': {
        minWidth: 0,
      },
      /* Make the search field shrink on small widths */
      '[class*="MTableToolbar-searchField"]': {
        minWidth: 0,
        flex: '0 1 220px', // lower to 200/180 if needed
        maxWidth: 220,
      },
      /* RIGHT side (export + your actions): never wrap */
      '[class*="MTableToolbar-actions"]': {
        display: 'flex',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        gap: 8,
      },
    },
  },
}));



/**
 * @param  {Object} props
 *
 */
const AisTable = props => {
  const { server } = useContext(serverContext);
  const { dispatchApp } = useContext(appContext);
  const [selectedRow, setSelectedRow] = useState(null);
  const tableTheme = useTableTheming(selectedRow);
  const [aisUpdate, setAisUpdate] = useState(Date.now());
  const [filter, setFilter] = useState(() => {
    const saved = localStorage.getItem('aisQueryFilter');
    // Ensure default aisType is 'vessels' if not found or invalid
    const parsed = saved !== null ? JSON.parse(saved) : {};
    return {
      mode: parsed.mode || 'last',
      aisType: parsed.aisType === 'aisBaseStations' ? 'aisBaseStations' : 'vessels', // Default to vessels
      info: parsed.info ?? true,
      retention: parsed.retention ?? 6,
      trail: parsed.trail ?? 10,
      mission: parsed.mission,
      start: parsed.start,
      end: parsed.end,
    };
  });
  const [queryFilter, setQueryFilter] = useState({});
  const [pauseUpdate, setPauseUpdate] = useState(false);
  const { isLoading, hasError, aisData } = useAisData(server.serverHost, server.token, queryFilter, aisUpdate);

  const [toolbarTimeControlOpen, setToolbarTimeControlOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false, type: null });

  // 🕒 Only run the refresh interval when not paused
  useEffect(() => {
    let id;
    if (!pauseUpdate) {
      id = setInterval(() => {
        setAisUpdate(Date.now());
      }, 10000);
    }
    return () => clearInterval(id);
  }, [pauseUpdate]);

  // Sync queryFilter whenever filter changes
  useEffect(() => {
    // const { mission, start, end, ...rest } = filter; // FIXME: Don't remember why this was implemented this way
    // setQueryFilter(rest);
   
    setQueryFilter(filter);
  }, [filter]);

  // Error handling
  useEffect(() => {
    if (hasError?.error) {
      console.error('Error fetching AIS data:', hasError.message);
      dispatchApp({
        type: 'SHOW_SNACKBAR_MESSAGE',
        payload: { message: `Error: ${hasError.message}`, severity: 'error' },
      });
    }
  }, [hasError, dispatchApp]);


  const actions = [];

  if (props.admin) {
    actions.push({
      icon: tableIcons.DeleteIcon,
      tooltip: 'Delete Ais',
      isFreeAction: true,
      onClick: (event, data) => {
        setConfirmState({ open: true, type: 'ais' });
      }
    })
    actions.push({
      icon: tableIcons.DeleteSweepIcon,
      tooltip: 'Delete vessel info',
      isFreeAction: true,
      onClick: (event, data) => {
        setConfirmState({ open: true, type: 'vessel' });
      }
    });
  }


  const classes = useStyles();

  const handleSelectedRow = (_, selectedRowData) => {
    setSelectedRow(selectedRowData);
    if (props.onAisTargetClick && selectedRowData?.lat && selectedRowData?.lon) {
      props.onAisTargetClick(selectedRowData); // Pass the whole row data
    }
  }

  const handleToolbarSelection = useCallback((sel) => {
    setFilter((prev) => {
      const updated = { ...prev, ...sel };
      if (sel.aisType && !['vessels', 'aisBaseStations'].includes(sel.aisType)) {
        updated.aisType = 'vessels';
      }
      localStorage.setItem('aisQueryFilter', JSON.stringify(updated));
      return updated;
    });
    setAisUpdate(Date.now());
  }, []);


  const handleTimeControlOpen = useCallback((opened) => {
    setToolbarTimeControlOpen(opened);
    setPauseUpdate(opened);
  }, []);


  // memorize toolbar so pause toggles don't remount it
  const toolbar = useMemo(() => (
    <AisTableToolbar
      data={aisData}
      filter={filter}
      timeControlOpened={toolbarTimeControlOpen}
      onSelection={handleToolbarSelection}
      onTimeControlOpen={handleTimeControlOpen}
    />
  ), [aisData, filter, toolbarTimeControlOpen, handleToolbarSelection, handleTimeControlOpen]);



  const columnsVessels = [
    {
      title: 'Sensor', width: '5%', filtering: true, field: 'sensorName',
      render: rowData =>
        <Tooltip title={`${rowData.platformName || 'N/A'}`}>
          <span>{`${rowData.sensorName || 'N/A'}`}</span>
        </Tooltip>
    },
    {
      title: 'MMSI',
      field: 'mmsi',
      width: '8%',
      filtering: true,
      render: rowData => (
        rowData.mmsi ? (
          <a
           // href={`https://www.marinetraffic.com/en/ais/details/ships/shipid:${rowData.mmsi}`}
            href={`https://www.marinetraffic.com`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking link
          >
            {rowData.mmsi}
          </a>
        ) : 'N/A'
      )
    },
    {
      title: 'Name', field: 'name', filtering: true, render: rowData => `${rowData.name || 'N/A'}`
    },
    {
      title: 'Type', field: 'vesselType', filtering: true, render: rowData => `${rowData.vesselType || 'N/A'}`
    },
    {
      title: 'Flag', field: 'countryCode', width: '4%', filtering: true,
      render: rowData => (
        rowData.countryCode && rowData.countryCode !== 'N/A' ? (
          <Tooltip title={rowData.country || 'N/A'}>
            <ReactCountryFlag countryCode={rowData.countryCode} svg />
          </Tooltip>
        ) : null
      )
    },
    {
      title: 'Timestamp', field: 'timestamp', width: '15%', filtering: true, defaultSort: 'desc', // Default sort descending
      render: rowData => {
        if (!rowData.timestamp) return 'N/A';
        try {
          return formatAisTimestamp(rowData.timestamp);
        } catch (e) {
          console.error("Error formatting timestamp:", rowData.timestamp, e);
          return 'Invalid Date';
        }
      }
    },
    {
      title: 'Lat', field: 'lat', width: '15%', filtering: true,
      render: rowData => `${convertLatitudeToDMS(rowData.lat) || 'N/A'}`
    },
    {
      title: 'Lon', field: 'lon', width: '15%', filtering: true,
      render: rowData => `${convertLongitudeToDMS(rowData.lon) || 'N/A'}`
    },
    {
      title: 'COG', field: 'cog', width: '5%', filtering: true,
      render: rowData => rowData.cog !== undefined && rowData.cog !== null ? rowData.cog : 'N/A'
    },
    {
      title: 'HDG', field: 'hdg', width: '5%', filtering: true,
      render: rowData => rowData.hdg !== undefined && rowData.hdg !== null && rowData.hdg !== 511 ? rowData.hdg : 'N/A'
    },
    {
      title: 'SOG', field: 'sog', width: '5%', filtering: true,
      render: rowData => rowData.sog !== undefined && rowData.sog !== null ? rowData.sog : 'N/A'
    },
  ]


  const columnsBaseStations = [
    {
      title: 'Sensor', width: '5%', filtering: true, field: 'sensorName',
      render: rowData =>
        <Tooltip title={`${rowData.platformName || 'N/A'}`}>
          <span>{`${rowData.sensorName || 'N/A'}`}</span>
        </Tooltip>
    },
    {
      title: 'MMSI', field: 'mmsi', width: '8%', filtering: true,
      render: rowData => rowData.mmsi || 'N/A'
    },
    {
      title: 'Name', field: 'name', filtering: true, render: rowData => `${rowData.name || 'N/A'}`
    },
    {
      title: 'Flag', field: 'countryCode', width: '4%', filtering: true,
      render: rowData => (
        rowData.countryCode && rowData.countryCode !== 'N/A' ? (
          <Tooltip title={rowData.country || 'N/A'}>
            <ReactCountryFlag countryCode={rowData.countryCode} svg />
          </Tooltip>
        ) : null
      )
    },
    {
      title: 'Timestamp', field: 'timestamp', width: '15%', filtering: true, defaultSort: 'desc', // Default sort descending
      render: rowData => {
        if (!rowData.timestamp) return 'N/A';
        try {
          return formatAisTimestamp(rowData.timestamp);
        } catch (e) {
          console.error("Error formatting timestamp:", rowData.timestamp, e);
          return 'Invalid Date';
        }
      }
    },
    {
      title: 'Lat', field: 'lat', width: '15%', filtering: true,
      render: rowData => `${convertLatitudeToDMS(rowData.lat) || 'N/A'}`
    },
    {
      title: 'Lon', field: 'lon', width: '15%', filtering: true,
      render: rowData => `${convertLongitudeToDMS(rowData.lon) || 'N/A'}`
    },
    {
      title: 'FixType', field: 'fixType', width: '8%', filtering: true,
      render: rowData => <FixTypeDisplay fixType={rowData.fixType} />
    }
  ]



  return (
    <div className={`${classes.root} ${classes.mtToolbarNoWrap}`}>
      <MaterialTable className={`${classes.root} ${classes.denseTable}`}
        icons={tableIcons}
        // Dynamically select columns based on filter.aisType
        columns={filter.aisType === 'aisBaseStations' ? columnsBaseStations : columnsVessels}
        data={aisData || []} // Ensure data is always an array
        title={toolbar}
        onRowClick={handleSelectedRow}
        options={{
          sorting: true,
          exportButton: true,
          exportFileName: `AisData_${filter.aisType}_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-')}`,
          cellStyle: { ...tableTheme.cellStyle, padding: '0.2em', fontSize: '0.9rem' },
          headerStyle: { ...tableTheme.headerStyle, fontWeight: 'bold' },
          filterRowStyle: tableTheme.filterRowStyle,
          selection: false,
          rowStyle: rowData => ({ ...tableTheme.rowStyle(rowData), cursor: 'pointer' }),
          filtering: false,
          pageSize: 5, // Sensible default page size
          pageSizeOptions: [5, 10, 20, 50, 100, 1000, 10000], // Common page size options      
          emptyRowsWhenPaging: false, // Don't show empty rows
          searchFieldStyle: { maxWidth: 220 }, // keep consistent with the style above
        }}
        actions={actions}
        components={{
          Toolbar: props => (
            <div>
              <MTableToolbar {...props} />
            </div>
          ),
          Pagination: PatchedPagination,
        }}
      />
      {isLoading && <LinearProgress />}
      <Dialog
        open={confirmState.open}
        onClose={() => setConfirmState({ open: false, type: null })}
      >
        <DialogTitle>
          {confirmState.type === 'ais' ? 'Delete AIS data?' : 'Delete vessel info?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmState.type === 'ais'
              ? 'This will remove AIS data. Do you want to continue?'
              : 'This will remove stored vessel information. Do you want to continue?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState({ open: false, type: null })} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmState.type === 'ais' && props.onDeleteAis) {
                props.onDeleteAis();
              } else if (confirmState.type === 'vessel' && props.onDeleteVesselInfo) {
                props.onDeleteVesselInfo();
              }
              setConfirmState({ open: false, type: null });
            }}
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}


export default AisTable;
